import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Color from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { BubbleMenu } from '@tiptap/react/menus';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { lowlight } from 'lowlight/lib/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiCheck, FiEye, FiSave } from 'react-icons/fi';
import { MdCode, MdFormatBold, MdFormatItalic } from 'react-icons/md';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchArticleVariants, publishPost, updatePost } from '../../api/admin';
import { resolveArticleSummary } from './articleSummary';
import EditorToolbar from './EditorToolbar';
import { getImageFilesFromTransfer, uploadArticleImage } from './imageUpload';
import styles from './NewPost.module.css';

const getStats = (text = '') => {
  const trimmed = text.trim();
  return {
    words: trimmed ? trimmed.split(/\s+/).length : 0,
    characters: text.length,
  };
};

const Post = ({ PId, PTitle, PContent, PSummary = '', PLanguage, PTranslationGroup = '' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(PId);
  const requestedGroup = PTranslationGroup || searchParams.get('translationGroup') || '';
  const requestedLanguage = PLanguage || searchParams.get('language') || 'en';
  const draftKey = useMemo(
    () => `codecognition-article-draft-${PId || `${requestedGroup || 'new'}-${requestedLanguage}`}`,
    [PId, requestedGroup, requestedLanguage],
  );
  const initialized = useRef(false);
  const editorRef = useRef(null);
  const [form, setForm] = useState({ title: '', summary: '', language: requestedLanguage, translationGroup: requestedGroup });
  const [summaryEdited, setSummaryEdited] = useState(false);
  const [articleVariants, setArticleVariants] = useState([]);
  const [revision, setRevision] = useState(0);
  const [stats, setStats] = useState({ words: 0, characters: 0 });
  const [saveState, setSaveState] = useState('saved');
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [contentError, setContentError] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imageUploadCount, setImageUploadCount] = useState(0);

  const uploadImage = useCallback(async (file) => {
    setImageUploadCount((count) => count + 1);
    try {
      return await uploadArticleImage(file);
    } finally {
      setImageUploadCount((count) => Math.max(0, count - 1));
    }
  }, []);

  const insertImageFiles = useCallback((files, position) => {
    if (!files.length) return false;

    setError('');
    Promise.all(files.map(async (file) => ({
      src: await uploadImage(file),
      alt: file.name || 'Pasted image',
    })))
      .then((images) => {
        const currentEditor = editorRef.current;
        if (!currentEditor || currentEditor.isDestroyed) return;

        const content = images.map(({ src, alt }) => ({
          type: 'image',
          attrs: { src, alt },
        }));
        const chain = currentEditor.chain().focus();
        if (Number.isInteger(position)) {
          const safePosition = Math.min(Math.max(position, 0), currentEditor.state.doc.content.size);
          chain.insertContentAt(safePosition, content).run();
        } else {
          chain.insertContent(content).run();
        }
        setStatusMessage(`${images.length} image${images.length > 1 ? 's' : ''} uploaded`);
      })
      .catch((uploadError) => {
        setError(uploadError.message || 'Image upload failed.');
      });

    return true;
  }, [uploadImage]);

  const markChanged = () => {
    if (!initialized.current) return;
    setSaveState('unsaved');
    setRevision((value) => value + 1);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false, link: false }),
      // Keep legacy article JSON readable. New pasted files are intercepted
      // below and uploaded, so normal editing no longer creates Base64 images.
      Image.configure({ inline: false, allowBase64: true }),
      CodeBlockLowlight.configure({ lowlight }),
      Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true }),
      TextStyle,
      Color,
    ],
    content: '<p></p>',
    editorProps: {
      attributes: {
        'aria-label': 'Article content',
        spellcheck: 'true',
      },
      handlePaste: (_view, event) => {
        const handled = insertImageFiles(getImageFilesFromTransfer(event.clipboardData));
        if (!handled) return false;
        event.preventDefault();
        return true;
      },
      handleDrop: (view, event) => {
        const dropPosition = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        const handled = insertImageFiles(getImageFilesFromTransfer(event.dataTransfer), dropPosition);
        if (!handled) return false;
        event.preventDefault();
        return true;
      },
    },
    onUpdate: ({ editor: current }) => {
      setStats(getStats(current.getText()));
      markChanged();
    },
  });

  useEffect(() => {
    editorRef.current = editor;
    return () => {
      editorRef.current = null;
    };
  }, [editor]);

  useEffect(() => {
    let active = true;
    fetchArticleVariants()
      .then((response) => {
        if (active) setArticleVariants(response.data?.data?.content || []);
      })
      .catch(() => {
        if (active) setArticleVariants([]);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!editor || initialized.current) return;

    let restored = false;
    try {
      const localDraft = JSON.parse(localStorage.getItem(draftKey) || 'null');
      if (localDraft?.content) {
        setForm({
          title: localDraft.title || '',
          summary: localDraft.summary || '',
          language: localDraft.language || requestedLanguage,
          translationGroup: localDraft.translationGroup || requestedGroup,
        });
        setSummaryEdited(Boolean(localDraft.summaryEdited));
        editor.commands.setContent(localDraft.content);
        setStatusMessage('Local draft restored');
        restored = true;
      }
    } catch {
      localStorage.removeItem(draftKey);
    }

    if (!restored) {
      setForm({
        title: PTitle || '',
        summary: PSummary || '',
        language: requestedLanguage,
        translationGroup: requestedGroup,
      });
      setSummaryEdited(false);
      editor.commands.setContent(PContent || '<p></p>');
    }

    setStats(getStats(editor.getText()));
    initialized.current = true;
    setSaveState('saved');
  }, [editor, draftKey, PTitle, PContent, PSummary, requestedLanguage, requestedGroup]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!previewMode);
  }, [editor, previewMode]);

  useEffect(() => {
    if (!editor || !initialized.current || saveState !== 'unsaved') return undefined;
    const timer = window.setTimeout(() => {
      localStorage.setItem(draftKey, JSON.stringify({
        ...form,
        summaryEdited,
        content: editor.getJSON(),
        savedAt: new Date().toISOString(),
      }));
      setSaveState('saved');
      setStatusMessage('Saved locally');
    }, 900);
    return () => window.clearTimeout(timer);
  }, [draftKey, editor, form, revision, saveState, summaryEdited]);

  useEffect(() => {
    const warnBeforeLeaving = (event) => {
      if (saveState !== 'unsaved') return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [saveState]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (name === 'summary') setSummaryEdited(true);
    setTitleError('');
    markChanged();
  };

  const saveLocalDraft = () => {
    if (!editor) return;
    localStorage.setItem(draftKey, JSON.stringify({
      ...form,
      summaryEdited,
      content: editor.getJSON(),
      savedAt: new Date().toISOString(),
    }));
    setSaveState('saved');
    setStatusMessage('Saved locally');
  };

  const validate = () => {
    let valid = true;
    if (!form.title.trim()) {
      setTitleError('Add a title before publishing.');
      valid = false;
    }
    if (!editor || editor.isEmpty) {
      setContentError('Write some content before publishing.');
      valid = false;
    }
    return valid;
  };

  const submit = async () => {
    if (imageUploadCount > 0) {
      setError('Please wait for image uploads to finish.');
      return;
    }
    if (!validate()) return;
    setSubmitting(true);
    setError('');
    const content = JSON.stringify(editor.getJSON());
    const payload = {
      title: form.title.trim(),
      summary: resolveArticleSummary({
        articleText: editor.getText(),
        summary: form.summary,
        summaryEdited,
      }),
      content,
      lng: form.language,
      translationGroup: form.translationGroup || null,
    };

    try {
      const response = isEdit
        ? await updatePost({ id: PId, ...payload })
        : await publishPost(payload);
      if (response.data.code !== 200) {
        setError(response.data.message || 'The article could not be saved.');
        return;
      }
      localStorage.removeItem(draftKey);
      initialized.current = false;
      navigate('/admin/posts');
    } catch (requestError) {
      setError(requestError.message || 'The backend is unavailable.');
    } finally {
      setSubmitting(false);
    }
  };

  const otherLanguage = form.language === 'zh' ? 'en' : 'zh';
  const sibling = form.translationGroup
    ? articleVariants.find((article) => article.translationGroup === form.translationGroup && article.lng === otherLanguage)
    : null;

  return (
    <div className={styles.editorPage}>
      <header className={styles.pageHeader}>
        <div>
          <RouterLink to="/admin/posts"><FiArrowLeft /> Articles</RouterLink>
          <h1>{isEdit ? 'Edit article' : 'New article'}</h1>
          <p>Write a focused technical note for CodeCognition.</p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryButton} onClick={() => setPreviewMode((value) => !value)}><FiEye /> {previewMode ? 'Continue editing' : 'Preview'}</button>
          <button type="button" className={styles.secondaryButton} onClick={saveLocalDraft}><FiSave /> Save draft</button>
          <button type="button" className={styles.primaryButton} disabled={submitting || imageUploadCount > 0} onClick={submit}>{imageUploadCount > 0 ? 'Uploading images…' : submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Publish'}</button>
        </div>
      </header>

      {(error || titleError || contentError) && (
        <div className={styles.errorMessage}>{error || titleError || contentError}</div>
      )}

      <div className={styles.editorGrid}>
        <div className={`${styles.editorCard} ${previewMode ? styles.previewCard : ''}`}>
          <input
            className={styles.titleInput}
            name="title"
            type="text"
            value={form.title}
            onChange={updateField}
            placeholder="Article title"
            readOnly={previewMode}
          />

          <EditorToolbar editor={editor} previewMode={previewMode} uploadImage={uploadImage} />

          {editor && !previewMode && (
            <BubbleMenu editor={editor} className={styles.bubbleMenu} shouldShow={({ from, to, editor: current }) => from !== to && !current.isActive('codeBlock')}>
              <button type="button" aria-label="Bold" className={editor.isActive('bold') ? styles.activeButton : ''} onClick={() => editor.chain().focus().toggleBold().run()}><MdFormatBold /></button>
              <button type="button" aria-label="Italic" className={editor.isActive('italic') ? styles.activeButton : ''} onClick={() => editor.chain().focus().toggleItalic().run()}><MdFormatItalic /></button>
              <button type="button" aria-label="Inline code" className={editor.isActive('code') ? styles.activeButton : ''} onClick={() => editor.chain().focus().toggleCode().run()}><MdCode /></button>
            </BubbleMenu>
          )}

          <EditorContent editor={editor} className={styles.tiptapEditor} />
          <footer className={styles.editorFooter}>
            <span>{stats.words} words</span>
            <span>{stats.characters} characters</span>
            <span className={imageUploadCount > 0 ? styles.unsaved : saveState === 'saved' ? styles.saved : styles.unsaved}>
              {imageUploadCount > 0 ? `Uploading ${imageUploadCount} image${imageUploadCount > 1 ? 's' : ''}…` : saveState === 'saved' ? <><FiCheck /> {statusMessage || 'Local draft ready'}</> : 'Unsaved changes'}
            </span>
          </footer>
        </div>

        <aside className={styles.settingsCard}>
          <p>ARTICLE SETTINGS</p>
          <label>
            <span>Language</span>
            <select name="language" value={form.language} onChange={updateField} disabled={Boolean(form.translationGroup)}>
              <option value="en">English</option>
              <option value="zh">中文</option>
            </select>
            {form.translationGroup && <small>Language is fixed inside an existing translation group.</small>}
          </label>
          <div className={styles.settingsBlock}>
            <span>Translation</span>
            {sibling ? (
              <RouterLink className={styles.translationLink} to={`/admin/posts/${sibling.id}/edit`}>
                Edit {otherLanguage === 'zh' ? '中文' : 'English'} version
              </RouterLink>
            ) : form.translationGroup ? (
              <RouterLink
                className={styles.translationLink}
                to={`/admin/posts/new?translationGroup=${encodeURIComponent(form.translationGroup)}&language=${otherLanguage}`}
              >
                Create {otherLanguage === 'zh' ? '中文' : 'English'} version
              </RouterLink>
            ) : (
              <p>A translation group will be created when this article is published.</p>
            )}
            {form.translationGroup && <p>Both language versions share one translation group automatically.</p>}
          </div>
          <label>
            <span>Summary</span>
            <textarea name="summary" value={form.summary} onChange={updateField} rows="6" maxLength="300" placeholder="Optional. The first 150 characters will be used if left empty." />
            <small>{form.summary.length}/300</small>
            <small>{summaryEdited ? 'Your custom summary will be saved.' : 'If unchanged, this will refresh from the latest article body when saved.'}</small>
          </label>

          <div className={styles.settingsBlock}>
            <span>Publishing</span>
            <strong>{isEdit ? 'Published article' : 'Publishes immediately'}</strong>
            <p>The current API has no server-side draft status yet. “Save draft” stores a private copy in this browser.</p>
          </div>
          <div className={styles.settingsBlock}>
            <span>Images</span>
            <strong>Paste, drop, upload, or URL</strong>
            <p>Paste screenshots or drag image files into the editor. They are uploaded to the Oracle server and inserted automatically.</p>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Post;
